import React, { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Row } from 'antd'

import AcceptExpenseApprovals from 'core/application/adminDashboard/reimbursements/perks/acceptEligibleExpenses'
import getPerksExpenseApprovals, {
  PerksExpenseApprovalResponse,
} from 'core/application/adminDashboard/reimbursements/perks/getPerksExpenseApprovals'
import rejectExpenseApprovals from 'core/application/adminDashboard/reimbursements/perks/rejectEligibleExpenses'
import OrderedPageFilter, { defaultFilter } from 'core/application/commons/orderedPageFilter'
import PagedList from 'core/application/commons/pagedList'

import { Button, Col, Icon, Popover, Skeleton, Table } from 'common/components'
import { parseZoneFormat } from 'common/dateFormatters'
import { useCommandRequestV2, useQueryRequestV2 } from 'common/hooks'
import { formatMoney } from 'common/moneyFormatter'

import ViewAttachmentsModal from '../../../../common/components/expenseAttachments/ViewAttachmentsModal'

import './index.less'

type PerksExpenseApprovalsProps = {
  planPeriodId: string
}

const PerksExpenseApprovals: React.FC<PerksExpenseApprovalsProps> = (props) => {
  const { t: translate } = useTranslation()
  const [filter, setFilter] = useState<OrderedPageFilter>({
    ...defaultFilter,
  })

  const {
    data: pagedExpenseApprovals,
    isLoading: isPagedExpenseApprovalsLoading,
    sendQuery: sendGetExpenseApprovalsQuery,
    reload: reloadExpenseApprovals,
  } = useQueryRequestV2<PagedList<PerksExpenseApprovalResponse>>({
    initialState: null,
    queryFn: () => getPerksExpenseApprovals(props.planPeriodId, filter),
  })

  const { sendCommand: sendAcceptExpenseApprovals, isLoading: isExpenseApprovalsLoading } = useCommandRequestV2(
    (expenseTypeId: string) => AcceptExpenseApprovals({ expenseId: expenseTypeId }),
  )

  const { sendCommand: sendRejectExpenseApprovals, isLoading: isRejectExpenseCommandLoading } = useCommandRequestV2(
    (expenseTypeId: string) => rejectExpenseApprovals({ expenseId: expenseTypeId }),
  )
  useEffect(() => {
    ;(async () => {
      await sendGetExpenseApprovalsQuery(() => getPerksExpenseApprovals(props.planPeriodId, filter))
    })()
  }, [filter])

  useEffect(() => {
    reloadExpenseApprovals()
  }, [props.planPeriodId])

  const viewAttachments = async (expenseId) => {
    setViewFilesVisible(true)
    setViewFilesForExpenseId(expenseId)
  }

  const getNotesIconContent = (notes: string): ReactNode => {
    const icon = (
      <div>
        <Icon type="fa fa-sticky-note-o" className="color-green-6" fontAwesome />
      </div>
    )

    return (
      <Popover
        title={translate('admin-dashboard.reimbursements.perks-expense-approval.notes')}
        placement="top"
        content={<div className="popover_width">{notes}</div>}
      >
        {' '}
        {icon}
      </Popover>
    )
  }
  const columns: any[] = [
    {
      title: translate('admin-dashboard.reimbursements.perks-expense-approval.first-name'),
      dataIndex: 'firstname',
      key: 'firstname',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: translate('admin-dashboard.reimbursements.perks-expense-approval.last-name'),
      dataIndex: 'lastname',
      key: 'lastname',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: translate('admin-dashboard.reimbursements.perks-expense-approval.category'),
      dataIndex: 'category',
      key: 'category',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: translate('admin-dashboard.reimbursements.perks-expense-approval.subcategory'),
      dataIndex: 'subCategory',
      key: 'subCategory',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: translate('admin-dashboard.reimbursements.perks-expense-approval.service-provider'),
      dataIndex: 'serviceProvider',
      key: 'serviceProvider',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: translate('admin-dashboard.reimbursements.perks-expense-approval.service-date'),
      dataIndex: 'serviceDate',
      key: 'serviceDate',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (text: string, record: any) => <span>{parseZoneFormat(record.serviceDate)}</span>,
    },
    {
      title: translate('admin-dashboard.reimbursements.perks-expense-approval.submitted'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (text: string, record: any) => <span>{parseZoneFormat(record.createdAt)}</span>,
    },
    {
      title: translate('admin-dashboard.reimbursements.perks-expense-approval.expense-amount'),
      dataIndex: 'amount',
      key: 'amount',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (text: string, record: any) => formatMoney(record.amount),
    },
    {
      title: translate('admin-dashboard.reimbursements.perks-expense-approval.notes'),
      dataIndex: 'notes',
      key: 'notes',
      align: 'center',
      render: (text: string) => {
        return getNotesIconContent(text)
      },
    },
    {
      title: translate('admin-dashboard.reimbursements.perks-expense-approval.receipt'),
      dataIndex: 'receipt',
      key: 'receipt',
      render: (text: string, record: any) =>
        record.requireUploadAttachment ? (
          <a>
            <Row gutter={8} onClick={() => viewAttachments(record.id)}>
              <Col>
                <Icon className="fas fa-file-image" />
              </Col>
              <Col>
                <div>View</div>
              </Col>
            </Row>
          </a>
        ) : (
          <h6 className=" center ">-</h6>
        ),
    },
  ]

  const columnToSort: any = columns.find((c) => c.key === filter.orderColumn)
  if (columnToSort) {
    columnToSort.defaultSortOrder = filter.orderDescending ? 'descend' : 'ascend'
  }

  const onTableFilterChanged = (pagination: any, filters: any, sorter: any) => {
    setFilter({
      ...filter,
      pageNumber: pagination.current,
      pageSize: pagination.pageSize,
      orderColumn: sorter.field,
      orderDescending: sorter.order !== 'ascend',
    })
  }
  const [totalApproved, setTotalApproved] = useState<number>(0)
  const [rowsSelected, setRowsSelected] = useState<number>(0)
  const [selectedExpense, setSelectedExpense] = useState<React.Key[]>([])
  const [viewFilesVisible, setViewFilesVisible] = useState<boolean>(false)
  const [viewFilesForExpenseId, setViewFilesForExpenseId] = useState<string>('')

  const rowSelection = {
    onChange: (selectedRowKeys: React.Key[], selectedRows: PerksExpenseApprovalResponse[]) => {
      let totalApproved = 0
      for (let i = 0; i < selectedRows.length; i++) {
        totalApproved += selectedRows[i].amount
      }
      setTotalApproved(totalApproved)
      setRowsSelected(selectedRows.length)
      setSelectedExpense(selectedRowKeys)
    },
  }

  const calculateTotalAmount = () => {
    let total = 0
    for (let i = 0; i < pagedExpenseApprovals?.items.length; i++) {
      total += pagedExpenseApprovals?.items[i].amount
    }
    return total
  }

  const acceptExpense = async () => {
    for (let i = 0; i < selectedExpense.length; i++) {
      const result = await sendAcceptExpenseApprovals(selectedExpense[i].toString())
      if (result.hasErrors) return
      reloadExpenseApprovals()
      setRowsSelected(0)
    }
  }

  const rejectExpense = async () => {
    for (let i = 0; i < selectedExpense.length; i++) {
      const result = await sendRejectExpenseApprovals(selectedExpense[i].toString())
      if (result.hasErrors) return
      reloadExpenseApprovals()
      setRowsSelected(0)
    }
  }

  return (
    <>
      <Row>
        <Col span={24}>
          <Row gutter={8} className={'vertically_center_elements mb-16'}>
            {rowsSelected !== 0 && (
              <>
                <Col xs={24} sm={8} md={6} lg={6} xl={4} xxl={3}>
                  <p className={'mt-16'}>
                    {rowsSelected} {translate('admin-dashboard.reimbursements.perks-expense-approval.selected-rows')}
                  </p>
                </Col>
                <Col xs={24} sm={8} md={6} lg={5} xl={4} xxl={3}>
                  <Button
                    type={'primary'}
                    className="p-0"
                    ghost
                    loading={isExpenseApprovalsLoading}
                    onClick={() => acceptExpense()}
                    style={{ width: '100%' }}
                  >
                    <Icon className={'fal fa-check m-8'} />
                    {translate('admin-dashboard.reimbursements.perks-expense-approval.approve-button')}
                  </Button>
                </Col>
                <Col xs={24} sm={8} md={6} lg={5} xl={4} xxl={3}>
                  <Button
                    className="p-0 color-red-6"
                    style={{ borderColor: 'red', color: 'red', width: '100%' }}
                    onClick={() => rejectExpense()}
                    loading={isRejectExpenseCommandLoading}
                  >
                    <Icon className={'fal fa-times m-8'} />
                    {translate('admin-dashboard.reimbursements.perks-expense-approval.reject-button')}
                  </Button>
                </Col>
              </>
            )}
          </Row>
        </Col>
      </Row>
      <Skeleton active loading={isPagedExpenseApprovalsLoading}>
        <Table
          rowSelection={rowSelection}
          dataSource={pagedExpenseApprovals?.items}
          onChange={onTableFilterChanged}
          loading={isPagedExpenseApprovalsLoading}
          columns={columns}
          scroll={{ x: 1000 }}
          pagination={{
            total: pagedExpenseApprovals?.totalItemCount,
            pageSize: pagedExpenseApprovals?.pageSize,
            current: pagedExpenseApprovals?.pageNumber,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '30', '40'],
          }}
          rowKey="id"
          footer={() => {
            return (
              <>
                <div className="total_reimbursements_due">
                  <Row gutter={32}>
                    <Col>
                      <h6>{translate('admin-dashboard.reimbursements.perks-expense-approval.total')}</h6>
                    </Col>
                    <Col>
                      <h6>
                        <b>{formatMoney(calculateTotalAmount())}</b>
                      </h6>
                    </Col>
                  </Row>
                </div>
                <div className="total_reimbursements_due">
                  <Row gutter={32}>
                    <Col>
                      <h6>{translate('admin-dashboard.reimbursements.perks-expense-approval.total-approved')}</h6>
                    </Col>
                    <Col>
                      <h6>
                        <b>{formatMoney(totalApproved)}</b>
                      </h6>
                    </Col>
                  </Row>
                </div>
              </>
            )
          }}
        />
      </Skeleton>

      {viewFilesForExpenseId && (
        <ViewAttachmentsModal
          visible={viewFilesVisible}
          onClose={() => setViewFilesVisible(false)}
          expenseId={viewFilesForExpenseId}
        />
      )}
    </>
  )
}

export default PerksExpenseApprovals
